// Loja VIP: cristais (doação via Mercado Pago) e tabuleiros pagos com cristais.
import { Application } from "pixi.js";
import { api, errorMessage, type Donation, type ShopBoard, type ShopResponse } from "../api";
import { ArenaLayer, boardsIndex } from "../game/arena";
import { brl, num, t, type MsgKey } from "../i18n";
import { navigate, type View } from "../router";
import { feature } from "../features";
import { loadSession as refreshSession } from "../session";
import { esc, screen } from "../ui";
import { boardDesc, boardName, packageName } from "../data";

export const CRYSTAL_ICON = `<svg class="crystal-icon" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1 14 6 8 15 2 6Z" fill="#7fe3ff"/><path d="M8 1 11 6H5Z" fill="#c8f6ff"/><path d="M2 6h12L8 15Z" fill="#3fb4e6"/><path d="M5 6 8 15 11 6Z" fill="#5ccdf5"/><path d="M8 1 14 6 8 15 2 6Z" fill="none" stroke="#0b3a52" stroke-width="1"/></svg>`;

type Tab = "boards" | "crystals" | "history";

export const shopView: View = (root, { query }) => {
  if (!feature("vipShop")) {
    navigate("/menu");
    return;
  }
  let tab: Tab = (query.get("aba") as Tab) || (query.get("doacao") ? "crystals" : "boards");
  let data: ShopResponse | null = null;
  let previewCleanup: (() => void) | null = null;
  let disposed = false;
  let poll = 0;

  root.innerHTML = screen(t("shop.title"), "/menu", `<div class="bw-textbox">${t("common.loading")}</div>`);
  const body = root.querySelector<HTMLElement>(".screen__body")!;

  const alertBox = () => body.querySelector<HTMLElement>("[data-alert]");
  const show = (msg: string, kind = "error") => {
    const a = alertBox();
    if (!a) return;
    a.className = `bw-alert bw-alert--${kind}`;
    a.textContent = msg;
    a.hidden = false;
  };

  async function load() {
    try {
      data = await api.shop();
      render();
    } catch (e) {
      body.innerHTML = `<div class="bw-alert bw-alert--error">${esc(errorMessage(e))}</div>`;
    }
  }

  function render() {
    if (!data || disposed) return;
    const idx = boardsCache;
    body.innerHTML = `
      <div class="shop-head bw-panel">
        <span class="crystals-balance">${CRYSTAL_ICON}<b data-balance>${num(data.crystals)}</b> ${t("shop.crystals")}</span>
        <div class="bw-tabs" role="tablist">
          ${(["boards", "crystals", "history"] as Tab[]).map((k) => `<button class="bw-btn ${tab === k ? "bw-btn--blue" : ""}" role="tab" aria-selected="${tab === k}" data-tab="${k}">${t(`shop.tab.${k}` as MsgKey)}</button>`).join("")}
        </div>
      </div>
      <div data-alert hidden></div>
      <div data-pane></div>`;
    const pane = body.querySelector<HTMLElement>("[data-pane]")!;
    if (tab === "boards") pane.innerHTML = boardsHtml(data.boards, idx);
    else if (tab === "crystals") pane.innerHTML = crystalsHtml(data);
    else {
      pane.innerHTML = `<div class="bw-textbox">${t("common.loading")}</div>`;
      void api.donations().then((r) => !disposed && tab === "history" && (pane.innerHTML = historyHtml(r.donations))).catch((e) => (pane.innerHTML = `<div class="bw-alert bw-alert--error">${esc(errorMessage(e))}</div>`));
    }
  }

  let boardsCache: Record<string, { file: string; fx: string | null }> = {};
  void boardsIndex().then((i) => {
    boardsCache = i;
    render();
  });

  function boardsHtml(boards: ShopBoard[], idx: typeof boardsCache): string {
    const noneEquipped = !boards.some((b) => b.equipped);
    const card = (b: ShopBoard) => {
      const file = idx[b.id]?.file;
      const action = b.equipped
        ? `<button class="bw-btn" disabled>${t("shop.equipped")}</button>`
        : b.owned
          ? `<button class="bw-btn bw-btn--blue" data-equip="${b.id}">${t("shop.equip")}</button>`
          : `<button class="bw-btn bw-btn--green" data-buy="${b.id}" ${data!.crystals < b.price ? 'aria-disabled="true"' : ""}>${CRYSTAL_ICON} ${num(b.price)}</button>`;
      return `<article class="bw-panel board-card ${b.equipped ? "on" : ""}">
        <button class="board-thumb fx-${b.fx ?? "none"}" data-preview="${b.id}" aria-label="${esc(t("shop.preview"))}: ${esc(boardName(b.id, b.name))}">
          ${file ? `<img src="/assets/${file}" alt="" loading="lazy">` : ""}<span class="fx-layer"></span>
          ${b.animated ? `<span class="board-badge">${t("shop.animated")}</span>` : ""}
        </button>
        <div class="board-info"><b>${esc(boardName(b.id, b.name))}</b><p class="bw-dim small">${esc(boardDesc(b.id, b.desc))}</p></div>
        <div class="board-actions">${action}</div>
      </article>`;
    };
    return `
      <p class="bw-dim small">${t("shop.boards.explain")}</p>
      <div class="board-grid">
        <article class="bw-panel board-card ${noneEquipped ? "on" : ""}">
          <div class="board-thumb board-thumb--default">${idx.padrao ? `<img src="/assets/${idx.padrao.file}" alt="" loading="lazy">` : ""}</div>
          <div class="board-info"><b>${t("shop.default")}</b><p class="bw-dim small">${t("shop.default.desc")}</p></div>
          <div class="board-actions">${noneEquipped ? `<button class="bw-btn" disabled>${t("shop.equipped")}</button>` : `<button class="bw-btn bw-btn--blue" data-equip="">${t("shop.equip")}</button>`}</div>
        </article>
        ${boards.map(card).join("")}
      </div>`;
  }

  function crystalsHtml(d: ShopResponse): string {
    const mode = d.payments === "dev" ? `<div class="bw-alert bw-alert--info">${t("shop.devMode")}</div>` : d.payments === "off" ? `<div class="bw-alert bw-alert--error">${t("shop.paymentsOff")}</div>` : "";
    return `${mode}
      <p class="bw-dim small">${t("shop.crystals.explain")}</p>
      <div class="pkg-grid">${d.packages.map((p, i) => `
        <article class="bw-panel pkg-card">
          <div class="pkg-gems">${CRYSTAL_ICON.repeat(i + 1)}</div>
          <b>${esc(packageName(p.id, p.name))}</b>
          <span class="pkg-amount">${num(p.crystals)} ${t("shop.crystals")}</span>
          ${p.bonus ? `<span class="pkg-bonus">+${num(p.bonus)} ${t("shop.bonus")}</span>` : `<span class="pkg-bonus pkg-bonus--none">&nbsp;</span>`}
          <button class="bw-btn bw-btn--green" data-donate="${p.id}" ${d.payments === "off" ? "disabled" : ""}>${t("shop.donate", { price: brl(p.priceCents) })}</button>
        </article>`).join("")}
      </div>
      <p class="bw-dim small">${t("shop.mp.note")}</p>`;
  }

  function historyHtml(list: Donation[]): string {
    if (!list.length) return `<div class="bw-textbox">${t("shop.history.empty")}</div>`;
    return `<table class="bw-table history"><thead><tr><th>${t("shop.history.date")}</th><th>${t("shop.crystals")}</th><th>${t("shop.history.value")}</th><th>${t("shop.history.status")}</th></tr></thead>
      <tbody>${list.map((d) => `<tr><td>${new Date(d.created_at).toLocaleDateString()}</td><td>${num(d.crystals)}</td><td>${brl(d.amount_cents)}</td><td>${t(`shop.status.${d.status}` as MsgKey)}</td></tr>`).join("")}</tbody></table>`;
  }

  // ------------------------------------------------------------- ações
  body.addEventListener("click", async (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-tab],[data-buy],[data-equip],[data-donate],[data-preview]");
    if (!el || !data) return;
    if (el.dataset.tab) {
      tab = el.dataset.tab as Tab;
      render();
      return;
    }
    if (el.dataset.preview) return openPreview(el.dataset.preview);
    if (el.getAttribute("aria-busy") === "true") return;
    el.setAttribute("aria-busy", "true");
    try {
      if (el.dataset.buy) {
        const b = data.boards.find((x) => x.id === el.dataset.buy)!;
        if (data.crystals < b.price) {
          show(t("shop.notEnough"));
          tab = "crystals";
          setTimeout(render, 1200);
          return;
        }
        if (!confirmBuy(b)) return;
        const r = await api.buyBoard(b.id);
        data.crystals = r.crystals;
        for (const x of data.boards) x.equipped = false;
        Object.assign(b, { owned: true, equipped: true });
        render();
        show(t("shop.bought", { name: boardName(b.id, b.name) }), "ok");
        void refreshSession();
      } else if (el.dataset.equip !== undefined) {
        const id = el.dataset.equip || null;
        await api.equipBoard(id);
        for (const x of data.boards) x.equipped = x.id === id;
        render();
      } else if (el.dataset.donate) {
        const r = await api.checkout(el.dataset.donate);
        // Mercado Pago (ou checkout de teste): volta para #/loja?doacao=<id>
        if (r.mode === "dev") navigate(`/loja/checkout-teste?doacao=${r.donationId}`);
        else location.href = r.url;
      }
    } catch (err) {
      show(errorMessage(err));
    } finally {
      el.removeAttribute("aria-busy");
    }
  });

  function confirmBuy(b: ShopBoard): boolean {
    // confirmação simples sem diálogo nativo bloqueante no mobile: segundo toque confirma
    const btn = body.querySelector<HTMLElement>(`[data-buy="${b.id}"]`)!;
    if (btn.dataset.confirm) return true;
    btn.dataset.confirm = "1";
    btn.innerHTML = t("shop.confirmBuy", { price: num(b.price) });
    setTimeout(() => {
      if (btn.isConnected) {
        delete btn.dataset.confirm;
        btn.innerHTML = `${CRYSTAL_ICON} ${num(b.price)}`;
      }
    }, 3000);
    return false;
  }

  // ------------------------------------------------- prévia animada (Pixi)
  async function openPreview(id: string) {
    previewCleanup?.();
    const b = data!.boards.find((x) => x.id === id)!;
    const modal = document.createElement("div");
    modal.className = "preview-modal";
    modal.innerHTML = `<div class="bw-panel preview-box"><div class="bw-panel__header">${esc(boardName(b.id, b.name))}</div><div class="preview-canvas"></div>
      <button class="bw-btn" data-close>${t("common.close")}</button></div>`;
    document.body.appendChild(modal);
    const host = modal.querySelector<HTMLElement>(".preview-canvas")!;
    const app = new Application();
    let alive = true;
    previewCleanup = () => {
      alive = false;
      modal.remove();
      try {
        app.destroy(true, { children: true });
      } catch {
        /* ainda inicializando */
      }
      previewCleanup = null;
    };
    modal.addEventListener("click", (ev) => {
      if (ev.target === modal || (ev.target as HTMLElement).closest("[data-close]")) previewCleanup?.();
    });
    await app.init({ resizeTo: host, backgroundAlpha: 0, antialias: false });
    if (!alive) return;
    host.appendChild(app.canvas);
    const arena = new ArenaLayer();
    app.stage.addChild(arena);
    arena.resize(host.clientWidth, host.clientHeight);
    await arena.set(id);
    app.ticker.add((tk) => arena.update(tk.deltaMS));
  }

  // --------------------------------------- retorno do Mercado Pago (polling)
  const donationId = query.get("doacao");
  async function watchDonation(id: string, tries = 0) {
    if (disposed) return;
    try {
      const r = await api.donation(id);
      if (r.donation.credited) {
        if (data) data.crystals = r.crystals;
        render();
        show(t("shop.donation.ok", { n: num(r.donation.crystals) }), "ok");
        void refreshSession();
        return;
      }
      if (["rejected", "cancelled"].includes(r.donation.status) || query.get("status") === "failure") {
        show(t("shop.donation.failed"));
        return;
      }
      show(t("shop.donation.waiting"), "info");
      if (tries < 40) poll = window.setTimeout(() => void watchDonation(id, tries + 1), 3000);
    } catch (e) {
      show(errorMessage(e));
    }
  }

  void load().then(() => {
    if (donationId) void watchDonation(donationId);
  });

  return () => {
    disposed = true;
    clearTimeout(poll);
    previewCleanup?.();
  };
};

/** Checkout de teste (dev, sem token do Mercado Pago): aprova ou recusa sem cobrar. */
export const devCheckoutView: View = (root, { query }) => {
  const id = query.get("doacao") ?? "";
  root.innerHTML = screen(t("shop.devCheckout.title"), "/loja", `
    <section class="bw-panel bw-stack">
      <div class="bw-alert bw-alert--info">${t("shop.devMode")}</div>
      <p>${t("shop.devCheckout.text")}</p>
      <div class="bw-row">
        <button class="bw-btn bw-btn--green" data-pay="1">${t("shop.devCheckout.approve")}</button>
        <button class="bw-btn bw-btn--red" data-pay="0">${t("shop.devCheckout.reject")}</button>
      </div>
      <div data-alert hidden></div>
    </section>`);
  root.querySelectorAll<HTMLElement>("[data-pay]").forEach((b) =>
    b.addEventListener("click", async () => {
      try {
        const approve = b.dataset.pay === "1";
        await api.devPay(id, approve);
        navigate(`/loja?doacao=${id}&status=${approve ? "approved" : "failure"}`);
      } catch (e) {
        const a = root.querySelector<HTMLElement>("[data-alert]")!;
        a.className = "bw-alert bw-alert--error";
        a.textContent = errorMessage(e);
        a.hidden = false;
      }
    }),
  );
};
